import type { Snapshot } from "@weather-deck/shared";
import { el } from "./dom";

const STALE_MS = 30 * 60 * 1000;

function fmtNum(n: number | null | undefined, unit: string, digits: number): string {
  return n == null ? "—" : `${n.toFixed(digits)}${unit}`;
}
function fmtDelta(a: number | null | undefined, b: number | null | undefined, unit: string): string {
  if (a == null || b == null) return "";
  const d = a - b;
  return `${d >= 0 ? "+" : ""}${d.toFixed(1)}${unit}`;
}

type Row = {
  label: string;
  unit: string;
  digits: number;
  deck: number | null;
  official?: { src: string; value: number | null };
  forecast?: number | null;
};

function tileFor(row: Row): HTMLElement {
  const children: Array<Node | null> = [
    el("h3", { text: row.label }),
    el("div", { className: "row" }, [
      el("span", { className: "src", text: "Deck" }),
      el("span", { className: "val", text: fmtNum(row.deck, row.unit, row.digits) })
    ])
  ];
  if (row.official) {
    children.push(el("div", { className: "row" }, [
      el("span", { className: "src", text: row.official.src }),
      el("span", { className: "val", text: fmtNum(row.official.value, row.unit, row.digits) }),
      el("span", { className: "delta", text: fmtDelta(row.deck, row.official.value, row.unit) })
    ]));
  }
  if (row.forecast !== undefined) {
    children.push(el("div", { className: "row" }, [
      el("span", { className: "src", text: "Forecast" }),
      el("span", { className: "val", text: fmtNum(row.forecast, row.unit, row.digits) })
    ]));
  }
  return el("article", { className: "tile" }, children);
}

function sensorStatus(snap: Snapshot): HTMLElement | null {
  const sensor = snap.sensor as unknown as { boxName?: string; lastReadingAt?: string } | null;
  if (!sensor?.lastReadingAt) {
    return el("p", { className: "sensor-status stale", text: "senseBox offline — no recent readings" });
  }
  const last = new Date(sensor.lastReadingAt);
  const time = last.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });
  const ageMs = Date.now() - last.getTime();
  const name = sensor.boxName ?? "senseBox";
  if (ageMs > STALE_MS) {
    const hours = ageMs / 3600000;
    const ageStr = hours >= 1 ? `${hours.toFixed(1)} h` : `${Math.round(ageMs / 60000)} min`;
    return el("p", { className: "sensor-status stale", text: `${name} · last reading ${time} (${ageStr} ago — box silent)` });
  }
  return el("p", { className: "sensor-status", text: `${name} · last reading ${time}` });
}

export function buildConcurTiles(snap: Snapshot): HTMLElement {
  const s = snap.sensor as unknown as { temp?: number; humidity?: number; pressure?: number; measurements?: { temperature?: number; humidity?: number; pressure?: number; uv?: number; lux?: number; pm25?: number; pm10?: number } } | null;
  const c = snap.weather?.current as unknown as { temp?: number; temperature?: number; humidity?: number; pressure?: number } | null;
  const air = snap.air;
  const rows: Row[] = [
    { label: "Temperature", unit: "°", digits: 1,
      deck: s?.temp ?? s?.measurements?.temperature ?? null,
      official: { src: "DWD", value: c?.temp ?? c?.temperature ?? null },
      forecast: c?.temp ?? c?.temperature ?? null },
    { label: "Humidity", unit: "%", digits: 0,
      deck: s?.humidity ?? s?.measurements?.humidity ?? null,
      official: { src: "DWD", value: c?.humidity ?? null },
      forecast: c?.humidity ?? null },
    { label: "Pressure", unit: "", digits: 0,
      deck: s?.pressure ?? s?.measurements?.pressure ?? null,
      official: { src: "DWD", value: c?.pressure ?? null },
      forecast: c?.pressure ?? null },
    { label: "PM2.5", unit: " µg/m³", digits: 1,
      deck: s?.measurements?.pm25 ?? null,
      official: { src: "UBA", value: air?.measurements.pm25 ?? null } },
    { label: "PM10", unit: " µg/m³", digits: 1,
      deck: s?.measurements?.pm10 ?? null,
      official: { src: "UBA", value: air?.measurements.pm10 ?? null } },
    { label: "UV", unit: " µW/cm²", digits: 0,
      deck: s?.measurements?.uv ?? null },
    { label: "Illuminance", unit: " lx", digits: 0,
      deck: s?.measurements?.lux ?? null }
  ];
  return el("section", { className: "concur" }, [
    ...rows.map(tileFor),
    sensorStatus(snap)
  ]);
}
